<template>
  <Page
    :loading="isLoadingPage"
    :spinning="isSpinning"
  >
    <VersionPickerContainer
      v-show="modeShowInfo"
      :item="item"
      :meta="meta"
      :item-exists="itemExists"
      :show-button-new-version="isCreateNewVersion && itemExists"
      :show-button-update="isUpdateVersion && itemExists"
      @new-version="setMode('newVersion')"
      @update="setMode('update')"
    >
      <template slot="sidebar">
        <iframe
          v-if="itemExists"
          :src="iframeLink"
          class="preview-iframe"
        />
      </template>
      <div v-if="itemExists">
        <Box v-if="itemExists">
          <h2>{{ item.info.name }}</h2>
          <BaseTable>
            <BaseTableHead>
              <BaseTableRow
                label="Meta"
                title
              />
            </BaseTableHead>
            <BaseTableGroup>
              <BaseTableRow label="ID / Navn">
                {{ item.info.name }}
              </BaseTableRow>
              <BaseTableRow label="Publiceringsdato">
                {{ item.info.publishDate | HumanDate }}
              </BaseTableRow>
              <BaseTableRow label="Version">
                {{ item.info.versionNumber }}
              </BaseTableRow>
              <BaseTableRow label="Redigeret af">
                {{ item.info.updatedBy }}
              </BaseTableRow>
              <BaseTableRow label="Intern note">
                <p
                  class="details__value"
                  v-html="item.info.internalNote || 'Intet'"
                />
              </BaseTableRow>
              <BaseTableRow label="Udsend notifikation">
                {{ item.info.sendNotification | IsTrue }}
              </BaseTableRow>
            </BaseTableGroup>
          </BaseTable>

          <BaseTable>
            <BaseTableHead>
              <BaseTableRow
                label="Detaljer"
                title
              />
            </BaseTableHead>
            <BaseTableGroup>
              <BaseTableRow label="Status">
                {{ item.info.deleted | IsDeleted }}
              </BaseTableRow>
              <BaseTableRow label="Indholdsstoffer">
                <p v-if="item.substances.length === 0">
                  Ingen tilknytning
                </p>
                <ul class="simpleList">
                  <li
                    v-for="substanceId in item.substances"
                    :key="substanceId"
                  >
                    <router-link
                      class="highlight underline"
                      :to="onVisitSubstance(substanceId)"
                    >
                      {{ getSingleSubstance(substanceId).name }}
                    </router-link>
                  </li>
                </ul>
              </BaseTableRow>
              <BaseTableRow label="Administrationsveje">
                <ul class="simpleList">
                  <li
                    v-for="roaId in item.routeOfAdministrations"
                    :key="roaId"
                  >
                    {{ getSingleRouteOfAdministration(roaId).name }}
                  </li>
                </ul>
              </BaseTableRow>
              <BaseTableRow label="Varenumre">
                <ul class="simpleList">
                  <li
                    v-for="productNumber in item.productNumbers"
                    :key="productNumber.id"
                  >
                    {{ productNumber.productNumber }}
                  </li>
                </ul>
              </BaseTableRow>
            </BaseTableGroup>
          </BaseTable>
        </Box>

        <Box v-if="item.localNotes.length || item.globalNotes.length">
          <div class="row">
            <div
              class="col-xs-6"
              v-if="item.globalNotes.length"
            >
              <h3>Globale noter</h3>
            </div>
            <div
              class="col-xs-6"
              v-if="item.localNotes.length"
            >
              <h3>Lokale noter</h3>
            </div>
          </div>
          <div class="row">
            <div
              class="col-xs-6"
              v-if="item.globalNotes.length"
            >
              <DisplayNote
                :note="note"
                v-for="note in item.globalNotes"
                :key="note.id"
              />
            </div>
            <div
              class="col-xs-6"
              v-if="item.localNotes.length"
            >
              <DisplayNote
                :note="note"
                v-for="note in item.localNotes"
                :key="note.id"
              />
            </div>
          </div>
        </Box>
      </div>
    </VersionPickerContainer>

    <section v-if="modeUpdate">
      <UpdatePreparation
        :form="item"
        :meta="meta"
        :from-import="fromImport"
        :action="mode"
        @saved="onSaved"
        @close="setMode('show')"
      />
    </section>
  </Page>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BaseTableHead from '../../components/BaseTable/BaseTableHead.vue';
import UpdatePreparation from '../../components/Forms/Preparations/UpdatePreparation.vue';
import DisplayNote from '../../components/DisplayNote.vue';
import BaseTableRow from '../../components/BaseTable/BaseTableRow.vue';
import BaseTableGroup from '../../components/BaseTable/BaseTableGroup.vue';
import BaseTable from '../../components/BaseTable/BaseTable.vue';
import VersionPickerContainer from '../../components/VersionPickerContainer.vue';
import Page from '../Page.vue';
import Box from '../../components/Box.vue';
import Pageload from '../../mixins/Pageload';
import ShowSingleVersion from '../../mixins/ShowSingleVersion';
import PreparationService from '../../services/PreparationService';
import IsTrue from '../../filters/isTrue';

export default {
    name: 'SinglePreparation',
    components: {
        Page,
        Box,
        VersionPickerContainer,
        BaseTable,
        BaseTableGroup,
        BaseTableRow,
        DisplayNote,
        UpdatePreparation,
        BaseTableHead,
    },
    mixins: [Pageload, ShowSingleVersion],
    filters: { IsTrue },
    props: {
        id: { type: String, required: true },
        fromImport: { type: Boolean, default: false },
        defaultMode: { type: String, default: 'show' },
        defaultItem: { type: Object, default: null },
        updatedRelations: { type: Object, default: () => {} },
    },
    data() {
        return {
            service: PreparationService,
            currentMedicineType: 'preparation',
        };
    },
    computed: {
        ...mapGetters({
            getAllGlobalNotesFromDatetime: 'globalnotes/getAllGlobalNotesFromDatetime',
            getSingleRouteOfAdministration: 'routeofadministration/getSingleRouteOfAdministration',
            getSingleSubstance: 'substances/getSingleSubstance',
        }),
    },
    methods: {
        ...mapActions({
            loadGlobalNotes: 'globalnotes/loadGlobalNotes',
            loadSubstances: 'substances/loadSubstances',
            loadRouteOfAdministration: 'routeofadministration/loadRouteOfAdministration',
        }),
        onVisitSubstance(substanceId) {
            const substance = this.getSingleSubstance(substanceId);
            if (Object.keys(substance).length === 0) {
                return {};
            }
            return { name: 'substances.single', params: { id: substance.id }, query: { date: this.selectedDateFormatted } };
        },
    },
    async created() {
        try {
            await this.loadGlobalNotes();
            await this.loadRouteOfAdministration();
            await this.loadSubstances();

            if (this.defaultItem) {
                this.item.info = { ...this.item.info, ...this.defaultItem };
            }


            if (this.fromImport && typeof this.updatedRelations === 'object') {
                this.item.substances = this.updatedRelations.substances.filter(sub => sub.removed === false).map(sub => sub.referenceID);
                this.item.routeOfAdministrations = this.updatedRelations.routeOfAdministrations.filter(item => item.removed === false).map(item => item.referenceID);
                this.item.productNumbers = this.updatedRelations.productNumbers.filter(item => item.removed === false).map(item => ({ productNumber: item.value }));
            }

            this.mode = this.defaultMode;
        } catch (error) {
            //
        }
    },
};
</script>

<style lang="scss" scoped>
.preview-iframe {
    width: 100%;
    height: 700px;
    box-shadow: 0 0 5px 2px rgba(46, 56, 77, 0.05);
}
</style>
